import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlayIcon,
  PauseIcon,
  ForwardIcon,
  BackwardIcon,
  ScissorsIcon,
  PlusIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { IVideoClip } from '@packages/global';

interface TimelineHeaderProps {
  videoClipsCount: number;
  isPlaying: boolean;
  currentTime: number;
  totalDuration: number;
  zoom: number;
  currentPlayingClip: IVideoClip | null;
  trackEnabled: { video: boolean; audio: boolean; text: boolean; bgmusic: boolean };
  onTogglePlay: () => void;
  onSeek: (time: number) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
}

export default function TimelineHeader({
  videoClipsCount,
  isPlaying,
  currentTime,
  totalDuration,
  zoom,
  currentPlayingClip,
  trackEnabled,
  onTogglePlay,
  onSeek,
  onZoomIn,
  onZoomOut
}: TimelineHeaderProps) {
  const { t } = useTranslation(['editor', 'common']);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center justify-between p-3 py-1 border-b border-gray-700">
      <div className="flex-1 flex items-center gap-4 min-w-0">
        <h3 className="text-lg font-semibold text-gray-50 whitespace-nowrap">{t('timeline.header.title', { defaultValue: t('tab.timeline') })}</h3>
        <span className="text-sm text-gray-400 whitespace-nowrap">{t('clipCount', { count: videoClipsCount })}</span>

        {isPlaying && currentPlayingClip && (
          <div className="text-sm text-blue-400 max-w-xs truncate">
            {t('nowPlaying')}: {trackEnabled.video && currentPlayingClip.videoUrl ? t('videoLabel') :
              trackEnabled.audio && (currentPlayingClip.scriptAudioUrl || currentPlayingClip.soundEffectAudioUrl) ? t('audioLabel') :
                currentPlayingClip.startImage || currentPlayingClip.endImage ? t('imageLabel') : t('descriptionLabel')} - {currentPlayingClip.title}
          </div>
        )}
      </div>

      <div className="flex items-center gap-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onSeek(Math.max(0, currentTime - 10))}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-all"
            title={t('rewind10s')}
          >
            <BackwardIcon className="w-5 h-5" />
          </button>
          <button
            onClick={onTogglePlay}
            className="p-2 bg-blue-600 hover:bg-blue-500 text-white rounded-full transition-all shadow-lg hover:shadow-blue-500/30 active:scale-95 flex items-center justify-center"
            title={isPlaying ? t('pause') : t('play')}
          >
            {isPlaying ? (
              <PauseIcon className="w-3 h-3 fill-current" />
            ) : (
              <PlayIcon className="w-3 h-3 fill-current ml-0.5" />
            )}
          </button>
          <button
            onClick={() => onSeek(Math.min(totalDuration, currentTime + 10))}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-all"
            title={t('forward10s')}
          >
            <ForwardIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="text-xs font-mono text-gray-300 whitespace-nowrap bg-gray-800/50 px-3 py-1 rounded-md border border-gray-700/50">
          <span className="font-medium">{formatTime(currentTime)}</span>
          <span className="mx-1">/</span>
          <span className="">{formatTime(totalDuration)}</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-end gap-2">
        {/* <button className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded-lg transition-colors">
          <ScissorsIcon className="w-4 h-4" />
        </button> */}
        {/* <button className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded-lg transition-colors">
          <PlusIcon className="w-4 h-4" />
        </button> */}
        <div className="flex items-center gap-1 ml-2">
          <button
            onClick={onZoomOut}
            className="p-1 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
          >
            <MagnifyingGlassMinusIcon className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-400 px-2">{Math.round(zoom * 100)}%</span>
          <button
            onClick={onZoomIn}
            className="p-1 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
          >
            <MagnifyingGlassPlusIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}